import './style.scss';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';

gsap.registerPlugin(ScrollTrigger);

// ─────────────────────────────────────────────────────────────
// GLOBAL STATE
// ─────────────────────────────────────────────────────────────
let lenis;
const appRoot = document.getElementById('app-root');

// ─────────────────────────────────────────────────────────────
// 1. LENIS SMOOTH SCROLL
// ─────────────────────────────────────────────────────────────
function initLenis() {
  if (lenis) { lenis.destroy(); }
  lenis = new Lenis({
    duration: 1.3,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    smoothWheel: true,
    wheelMultiplier: 0.9,
  });
  // Single RAF driver via GSAP ticker only — no separate requestAnimationFrame loop
  // (double-calling lenis.raf breaks ScrollTrigger scroll tracking)
  gsap.ticker.add((time) => { lenis.raf(time * 1000); });
  gsap.ticker.lagSmoothing(0);
  lenis.on('scroll', ScrollTrigger.update);
}

// ─────────────────────────────────────────────────────────────
// 2. MARKDOWN PARSER
// ─────────────────────────────────────────────────────────────
function parseMarkdown(md) {
  if (!md) return '';

  // Split into blocks by double newline first
  const rawBlocks = md.split(/\n{2,}/);
  const htmlParts = [];

  rawBlocks.forEach(block => {
    const trimmed = block.trim();
    if (!trimmed) return;

    // ### Sub-heading — rendered as editorial section marker
    if (/^###\s/.test(trimmed)) {
      const text = trimmed.replace(/^###\s+/, '');
      htmlParts.push(`<h3 class="lfb-subheading">${text}</h3>`);

    // ## H2 heading
    } else if (/^##\s/.test(trimmed)) {
      const text = trimmed.replace(/^##\s+/, '');
      htmlParts.push(`<h2>${text}</h2>`);

    // Ordered list items: 1. 2. 3. etc
    } else if (/^\d+\.\s/.test(trimmed)) {
      const items = trimmed.split(/\n/).filter(l => /^\d+\.\s/.test(l.trim()));
      const lis = items.map(l => {
        const text = l.replace(/^\d+\.\s+/, '');
        return `<li><div class="lfb-step-content">${applyInlineMarkdown(text)}</div></li>`;
      }).join('');
      htmlParts.push(`<ol class="lfb-steps">${lis}</ol>`);

    // Bullet list items: - or *
    } else if (/^[*-]\s/.test(trimmed)) {
      const lines = trimmed.split(/\n/).filter(l => /^[*-]\s/.test(l.trim()));
      const lis = lines.map(l => {
        const text = l.replace(/^[*-]\s+/, '');
        return `<li><div class="lfb-list-content">${applyInlineMarkdown(text)}</div></li>`;
      }).join('');
      htmlParts.push(`<ul class="lfb-list">${lis}</ul>`);

    // Paragraph
    } else {
      // Check if paragraph contains a key stat pattern (**XX%** or **X-X hours**)
      const statMatch = trimmed.match(/\*\*([^*]{2,30}(?:%|hours?|cycles?|patients?|procedures?)[^*]*)\*\*/i);
      if (statMatch && trimmed.length < 300) {
        // Short stat-heavy paragraph → render as pull-stat block
        htmlParts.push(`<div class="lfb-stat-callout">${applyInlineMarkdown(trimmed)}</div>`);
      } else {
        // Regular paragraph — keep it short and scannable
        htmlParts.push(`<p>${applyInlineMarkdown(trimmed)}</p>`);
      }
    }
  });

  return htmlParts.join('');
}

function applyInlineMarkdown(text) {
  // Bold → strong with cyan color
  return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
}

// ─────────────────────────────────────────────────────────────
// 3. DATA MAPS
// ─────────────────────────────────────────────────────────────
const routeImages = {
  home:               '/images/home_hero.png',
  'breast-endocrine': '/images/spec_breast.png',
  'laser-proctology': '/images/spec_proctology.png',
  'general-surgery':  '/images/spec_general.png',
  'dr-anukriti-sood':     '/images/dr_anukriti_sood.png',
  'dr-mansimrat-p-singh': '/images/dr_mansimrat_paul_singh.jpeg',
  'dr-bk-sood':           '/images/dr_bk_sood.png',
  'contact':          '/images/ot.png',
};

const longFormVisuals = {
  home: [
    { image: '/images/home_hero.png',  badge: 'ERAS Protocol',          title: 'Accelerated Recovery',     desc: 'Governed by strict Enhanced Recovery After Surgery pathways — safe discharge within 4–8 hours.' },
    { image: '/images/laparoscopic.png',    badge: 'Advanced Portfolios',      title: 'Minimally Invasive',       desc: 'Replacing open incisions with laser fibers, microwave ablation probes and keyhole trocars.' },
    { image: '/images/ot.png', badge: 'Technology Benchmarks',    title: 'Laminar Air Flow OT',      desc: 'Positive-pressure AHU operating theaters with HEPA filtration: zero cross-contamination.' },
    { image: '/images/laser.png', badge: 'Integrated Care',          title: 'Pre-Op to Tele-Monitoring',desc: 'Seamless patient optimization mapped to 24-hour post-operative monitoring calls.' },
  ],
  'breast-endocrine': [
    { image: '/images/spec_breast.png', badge: 'Microwave Ablation',       title: 'Scarless Nodule Removal',  desc: 'Ultrasound-guided thermal energy targets benign thyroid & breast lesions with zero scarring.' },
    { image: '/images/laparoscopic.png', badge: '4K Endoscopy',             title: 'Transaxillary Access',    desc: 'Thyroid resections via hidden armpit incisions to preserve aesthetics.' },
    { image: '/images/ot.png', badge: 'Diabetic Foot Protocol',   title: 'Integrated Limb Salvage',  desc: 'Multidisciplinary vascular mapping and debridement to prevent amputations.' },
  ],
  'laser-proctology': [
    { image: '/images/laser.png',    badge: '1470nm Diode Laser',      title: 'Hemorrhoidoplasty (LHP)',   desc: 'Radial laser fibers collapse piles nodes from within, preserving mucosa and sphincter.' },
    { image: '/images/laparoscopic.png',    badge: 'VAAFT & FiLaC',           title: 'Sphincter Preservation',   desc: 'High-definition micro-endoscopes map fistulas, then sealed with radial laser energy.' },
    { image: '/images/ot.png', badge: 'Keyhole Abdominal',        title: 'TEP & TAPP Hernia',        desc: 'Three micro-incisions allow secure mesh placement without muscle tension.' },
  ],
  'general-surgery': [
    { image: '/images/laparoscopic.png',    badge: 'Minimal Access',     title: 'Laparoscopic Surgery', desc: 'Advanced laparoscopic solutions for gallbladder, appendix and hernia with same-day discharge.' },
    { image: '/images/ot.png', badge: 'Wound Care',           title: 'Diabetic Foot & Ulcers', desc: 'Specialized surgical debridement and vacuum-assisted closure for chronic non-healing wounds.' },
    { image: '/images/spec_general.png', badge: 'General Procedures',     title: 'Lumps & Cysts',      desc: 'Painless excision of lipomas, sebaceous cysts and minor swellings under local anesthesia.' },
  ]
};

const doctorMetadata = {
  'dr-bk-sood':           { name: 'Dr. B. K. Sood',          specialty: 'Senior Consultant Surgeon',               credentials: 'MBBS, MS (General Surgery), FICS (USA)',  badge: 'Senior Consultant',  image: routeImages['dr-bk-sood'], phone: '98294 57467', phoneRaw: '9829457467' },
  'dr-mansimrat-p-singh': { name: 'Dr. Mansimrat P. Singh',  specialty: 'General, Laparoscopic & Minimal Access Surgeon', credentials: 'MBBS, MS, FNB (Minimal Access Surgery), MRCS (U.K.)',       badge: 'Laser Fellow',       image: routeImages['dr-mansimrat-p-singh'], phone: '78911 33200', phoneRaw: '7891133200' },
  'dr-anukriti-sood':     { name: 'Dr. Anukriti Sood',       specialty: 'Breast, Thyroid & Diabetic Foot Surgeon', credentials: 'MBBS, MS, MCh (Breast & Endocrine Surgery)',     badge: 'AIIMS Alumnus',      image: routeImages['dr-anukriti-sood'], phone: '80582 33200', phoneRaw: '8058233200' },
};

const bentoSpecialties = [
  { title: 'Breast & Endocrine Surgery',     desc: 'Pioneering microwave ablation (MWA) for benign breast nodules and robotic scarless thyroidectomies under AIIMS alumni expertise.',               path: '/specialty/breast-endocrine/', tag: 'Microwave Ablation',   icon: '🧬', class: 'bento-featured', image: '/images/spec_breast.png' },
  { title: 'Laser Proctology & Laparoscopy', desc: '1. Advanced Diode Laser Hemorrhoidoplasty , fistula procedures.<br>2. Laparoscopic Procedures with 6-8 hrs Discharge',       path: '/specialty/laser-proctology/', tag: 'Video-Assisted Laser', icon: '⚡', class: 'bento-h2',       image: '/images/spec_proctology.png' },
  { title: 'Advanced Wound Management',   desc: 'Advanced Wound Care with sterile dressings and Negative Pressure dressing for quick wound healing.',         path: '/specialty/general-surgery/',  tag: 'Senior Specialist',    icon: '🩹', class: 'bento-w2',       image: '/images/ot.png' },
  { title: 'General Surgery',                desc: '1. Hernia, Appendix, Gallbladder Surgery & Hysterectomy.<br>2. Advanced Fissure & Fistula procedures.',                          path: '/specialty/general-surgery/',  tag: 'General Surgery',      icon: '🏥', class: '',               image: '/images/laparoscopic.png' },
];

const deptDoctorsMap = {
  'Breast & Endocrine Surgery':     [{ slug: 'dr-anukriti-sood',     name: 'Dr. Anukriti Sood (Breast, Thyroid Surgeon)' }, { slug: 'dr-bk-sood', name: 'Dr. B. K. Sood (General Surgeon)' }],
  'Laser Proctology & Laparoscopy': [{ slug: 'dr-mansimrat-p-singh', name: 'Dr. Mansimrat P. Singh (General, Laparoscopic & Minimal Access Surgeon)' }, { slug: 'dr-bk-sood', name: 'Dr. B. K. Sood (General Surgeon)' }],
  'Advanced Wound Management':   [{ slug: 'dr-bk-sood',           name: 'Dr. B. K. Sood (General Surgeon)' }, { slug: 'dr-anukriti-sood', name: 'Dr. Anukriti Sood (Breast, Thyroid Surgeon)' }, { slug: 'dr-mansimrat-p-singh', name: 'Dr. Mansimrat P. Singh (General, Laparoscopic & Minimal Access Surgeon)' }],
  'General Surgery':             [{ slug: 'dr-bk-sood',           name: 'Dr. B. K. Sood (General Surgeon)' }, { slug: 'dr-anukriti-sood', name: 'Dr. Anukriti Sood (Breast, Thyroid Surgeon)' }, { slug: 'dr-mansimrat-p-singh', name: 'Dr. Mansimrat P. Singh (General, Laparoscopic & Minimal Access Surgeon)' }],
};

// ─────────────────────────────────────────────────────────────
// 4. SVG ICONS
// ─────────────────────────────────────────────────────────────
const ico = {
  arrow:      `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>`,
  arrowUR:    `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/></svg>`,
  check:      `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`,
  phone:      `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.11 12 19.79 19.79 0 0 1 1.04 3.42 2 2 0 0 1 3 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>`,
  calendar:   `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>`,
  star:       `<svg viewBox="0 0 24 24" fill="#F5A623" stroke="#F5A623" stroke-width="0"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>`,
  clock:      `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`,
  shield:     `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>`,
  home:       `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>`,
  search:     `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>`,
  plus:       `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>`,
  location:   `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2a8 8 0 0 0-8 8c0 5.25 8 12 8 12s8-6.75 8-12a8 8 0 0 0-8-8z"/><circle cx="12" cy="10" r="3"/></svg>`,
};

// ─────────────────────────────────────────────────────────────
// 5. SECTION RENDERERS
// ─────────────────────────────────────────────────────────────

/* ── HERO ── */
function renderHero(hero, imageKey) {
  const bg  = routeImages[imageKey] || routeImages.home;
  const h1  = hero.h1 || 'Advanced Surgical Care in Jaipur.';
  const sub = hero.subheadline || 'Expert surgical care delivered within a single day.';
  const cta = hero.cta || 'Book Consultation';
  const words = h1.split(' ');
  const mid   = Math.ceil(words.length / 2);
  const line1 = words.slice(0, mid).join(' ');
  const line2 = words.slice(mid).join(' ');

  const isHome = imageKey === 'home';

  return /* html */`
<section class="hero-section ${isHome ? 'hero-section--home' : 'hero-section--inner'}">
  <div class="hero-bg-wrapper" id="hero-bg">
    <img src="${bg}" alt="${h1}" fetchpriority="high"
         onload="this.closest('.hero-bg-wrapper').classList.add('loaded')">
  </div>
  <div class="hero-overlay"></div>
  <div class="hero-noise"></div>
  <div class="hero-glow hero-glow--cyan"></div>
  <div class="hero-glow hero-glow--coral"></div>

  <div class="hero-content">
    <!-- Left: Text -->
    <div class="hero-text-col">
      <div class="hero-badge" id="hero-badge">
        <span class="badge-pulse"></span>
        MDCC · Kalwar Road, Jaipur
      </div>

      <h1 class="hero-h1" id="hero-h1">
        <span class="h1-line">${line1}</span>
        <span class="h1-accent">${line2}</span>
      </h1>

      <p class="hero-sub" id="hero-sub">${sub}</p>

      <div class="hero-cta-stack" id="hero-ctas">
        <a href="/contact/" class="btn btn-primary" id="hero-cta-primary">
          ${ico.calendar} ${cta}
        </a>
        <a href="/doctors/" class="btn btn-secondary" id="hero-cta-secondary">
          ${ico.search} Explore Specialists
        </a>
      </div>

      <div class="hero-trust-row" id="hero-trust">
        <span class="trust-item">${ico.check} Same-Day Discharge</span>
        <span class="trust-item">${ico.check} 5 Specialties</span>
        <span class="trust-item">${ico.check} Robotic Surgery</span>
        <span class="trust-item">${ico.check} Sterile Environment</span>
      </div>
    </div>

    <!-- Right: Stats Card -->
    ${isHome ? `
    <div class="hero-stats-card" id="hero-stats">
      <div class="hero-stat">
        <div class="stat-number"><span class="count-up" data-target="65">0</span><span>%+</span></div>
        <div class="stat-label">Surgeries Done as Day Care</div>
        <div class="stat-desc">Of all elective procedures can be safely completed without a hospital stay.</div>
      </div>
      <div class="hero-stat">
        <div class="stat-number"><span class="count-up" data-target="24">0</span><span>hr</span></div>
        <div class="stat-label">In & Out Same Day</div>
        <div class="stat-desc">Pain Free Surgery</div>
      </div>
      <div class="hero-stat">
        <div class="stat-number"><span class="count-up" data-target="3">0</span></div>
        <div class="stat-label">Senior Surgeons</div>
        <div class="stat-desc">Board-certified surgeons trained at AIIMS, FNB, MRCS and MS/MCh programmes.</div>
      </div>
      <div class="hero-stat">
        <div class="stat-number"><span>0%</span></div>
        <div class="stat-label">Hospital-Acquired Infections</div>
        <div class="stat-desc">Isolated day-care units and Laminar Air Flow OTs eliminate cross-infection.</div>
      </div>
    </div>` : ''}
  </div>

  <div class="hero-scroll-hint" aria-hidden="true">
    <div class="scroll-wheel"></div>
    <span>Scroll</span>
  </div>
</section>`;
}

/* ── MARQUEE TRUST STRIP ── */
function renderMarquee() {
  const pills = [
    { label: 'Same-Day Discharge',      icon: ico.clock },
    { label: 'Robotic Surgery',         icon: ico.shield },
    { label: 'Laser Proctology',        dot: true },
    { label: 'Microwave Ablation',      dot: true },
    { label: 'Robotic Thyroidectomy',   dot: true },
    { label: 'Endoscopic Procedures',   dot: true },
    { label: 'Zero Cross-Infection',    icon: ico.check },
    { label: 'Laparoscopic Surgery',    dot: true },
    { label: 'Bariatric Surgery',       dot: true },
    { label: 'TEP & TAPP Hernia',       dot: true },
    { label: "Jaipur's Day Care Hub",   icon: ico.location },
  ];

  const mkPill = p => `
    <span class="marquee-pill">
      ${p.icon || '<span class="pill-dot"></span>'}
      ${p.label}
    </span>`;
  const track = pills.map(mkPill).join('');

  return /* html */`
<div class="trust-marquee-section" aria-hidden="true">
  <div class="trust-marquee-inner">
    <div class="trust-marquee-track">${track}</div>
    <div class="trust-marquee-track">${track}</div>
  </div>
</div>`;
}

/* ── PARADIGM / ABOUT SECTION ── */
function renderParadigm() {
  return /* html */`
<section class="paradigm-section" id="paradigm">
  <div class="paradigm-grid">

    <!-- Visual column — badges are OUTSIDE .paradigm-image-stack so overflow:hidden won't clip them -->
    <div class="paradigm-visual" id="paradigm-visual">
      <div class="paradigm-image-stack">
        <img
          src="https://images.unsplash.com/photo-1581594693702-fbdc51b2763b?auto=format&fit=crop&w=900&q=80"
          alt="MDCC state-of-the-art surgical suite"
          loading="lazy">
        <div class="paradigm-img-overlay"></div>
      </div>
      <!-- Stat badges live OUTSIDE the overflow:hidden image-stack -->
      <div class="paradigm-badge-float paradigm-badge-float--tl" id="pfloat-tl">
        <div class="float-badge-num">24<span>hr</span></div>
        <div class="float-badge-label">Discharge Cycle</div>
      </div>
      <div class="paradigm-badge-float paradigm-badge-float--br" id="pfloat-br">
        <div class="float-badge-num">100<span>%</span></div>
        <div class="float-badge-label">Sterile Environment</div>
      </div>
    </div>

    <!-- Text column -->
    <div class="paradigm-text" id="paradigm-text">
      <span class="section-tag">Why Choose MDCC</span>
      <h2>Surgery in the Morning.<br><em class="h2-accent">Home by Evening.</em></h2>
      <p class="paradigm-lead">
        Major surgical procedures completed and patients safely discharged within a
        <strong>single day</strong> — at Kalwar Road, Jhotwara, Jaipur.
      </p>

      <!-- Metrics -->
      <div class="paradigm-metrics" id="paradigm-metrics">
        <div class="p-metric">
          <div class="p-num"><span class="count-up" data-target="65">0</span>%</div>
          <div class="p-label">Day-Care Eligible</div>
        </div>
        <div class="p-metric">
          <div class="p-num">4–8h</div>
          <div class="p-label">Discharge Time</div>
        </div>
        <div class="p-metric">
          <div class="p-num">0%</div>
          <div class="p-label">Cross-Infection</div>
        </div>
      </div>

      <!-- Capsules -->
      <div class="paradigm-capsules" id="paradigm-capsules">
        <div class="pcap">
          <div class="pcap-icon">${ico.clock}</div>
          <div>
            <h4>Same-Day Discharge</h4>
            <p>Keyhole incisions and refined anaesthesia allow immediate recovery at home.</p>
          </div>
        </div>
      </div>
    </div>

  </div>
</section>`;
}

/* ── BENTO GRID ── */
function renderBento() {
  return /* html */`
<section class="specialties-section">
  <div class="specialties-inner">

    <div class="section-header" id="bento-header">
      <span class="section-tag">Our Specialties</span>
      <h2>Expert Opinions & Treatments</h2>
      <p>Four specialist surgical departments under one roof at Kalwar Road, Indrapura — delivering advanced,
         same-day procedures with AIIMS-trained faculty across Jaipur.</p>
    </div>

    <div class="bento-grid" id="bento-grid">
      ${bentoSpecialties.map(item => {
        const bg = item.image || '';
        return /* html */`
        <a href="${item.path}" class="bento-item ${item.class} has-bg"
           style="--bg-url:url('${bg}')" aria-label="${item.title}">
          <div class="bento-top">
            <span class="bento-tag">${item.tag}</span>
          </div>
          <div>
            <span class="bento-icon">${item.icon}</span>
            <div class="bento-meta">
              <h3>${item.title}</h3>
              <p>${item.desc}</p>
            </div>
            <div class="bento-link">
              Explore Department
              <span class="link-arrow">${ico.arrowUR}</span>
            </div>
          </div>
        </a>`;
      }).join('')}
    </div>
  </div>
</section>`;
}

/* ── DOCTOR CARDS — TYPOGRAPHIC MONOGRAM SYSTEM ── */
function renderDoctors() {
  const entries = Object.entries(doctorMetadata);
  return /* html */`
<section class="doctors-section">
  <div class="doctors-inner">

    <div class="section-header" id="doctors-header">
      <span class="section-tag">Our Specialists</span>
      <h2>Meet Our Faculty</h2>
      <p>Board-certified surgeons, AIIMS alumni, and specialist consultants — delivering advanced same-day procedures at MDCC.</p>
    </div>

    <div class="doctors-grid" id="doctors-grid">
      ${entries.map(([slug, doc]) => /* html */`
      <a href="/doctor/${slug}/" class="doctor-card" aria-label="View profile of ${doc.name}">
        <div class="doctor-photo-frame">
          <img src="${doc.image}" alt="${doc.name}" class="doctor-photo" loading="lazy">
          <div class="doctor-photo-overlay"></div>
          <div class="doctor-badge-float">${doc.badge}</div>
        </div>
        <div class="doctor-info-wrapper">
          <div>
            <div class="doc-name">${doc.name}</div>
            <div class="doc-credentials">${doc.credentials}</div>
            <div class="doc-specialty">${doc.specialty}</div>
          </div>
          <div class="doc-action">
            <span class="action-label">View Profile</span>
            <span class="action-circle">${ico.arrowUR}</span>
          </div>
        </div>
      </a>`).join('')}
    </div>

    <div class="doctor-dots">
      ${entries.map((_, idx) => /* html */`
        <button class="doctor-dot ${idx === 0 ? 'active' : ''}" data-index="${idx}" aria-label="Go to specialist slide ${idx + 1}"></button>
      `).join('')}
    </div>

  </div>
</section>`;
}

/* ── LONG-FORM CONTENT BLOCKS — EDITORIAL LAYOUT ── */
// Redesigned: narrow reading rail + floating image + stat callouts
// No more wall-of-text. Each block is scannable, airy, editorial.
function getHighlightCardHTML(slug, index) {
  if (slug !== 'home') {
    return /* html */`
      <div class="lfb-highlight-card">
        <div class="lfb-card-title">MDCC Clinical Standard</div>
        <div class="lfb-card-specs">
          <div class="lfb-spec-item">
            <div class="lfb-spec-icon">🛡️</div>
            <div>
              <strong>Same-Day Discharge</strong>
              <span>Advanced day-care surgical pathways</span>
            </div>
          </div>
          <div class="lfb-spec-item">
            <div class="lfb-spec-icon">🧪</div>
            <div>
              <strong>Laminar Air Flow</strong>
              <span>Class 100 positive-pressure sterility</span>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  if (index === 0) {
    return /* html */`
      <div class="lfb-highlight-card">
        <div class="lfb-card-title">ERAS Care Benchmarks</div>
        <div class="lfb-card-stats">
          <div class="lfb-cstat">
            <div class="lfb-cnum">65%<span class="lfb-cnum-suffix">+</span></div>
            <div class="lfb-clabel">Day-Care Eligible</div>
          </div>
          <div class="lfb-cstat">
            <div class="lfb-cnum">4-8<span class="lfb-cnum-suffix">h</span></div>
            <div class="lfb-clabel">Avg. Discharge</div>
          </div>
          <div class="lfb-cstat">
            <div class="lfb-cnum">0%</div>
            <div class="lfb-clabel">Cross-Infection</div>
          </div>
        </div>
      </div>
    `;
  }

  if (index === 1) {
    return /* html */`
      <div class="lfb-highlight-card">
        <div class="lfb-card-title">Super-Specialty Faculty</div>
        <div class="lfb-card-list">
          <div class="lfb-clist-item">
            <span class="lfb-clist-dot"></span>
            <div>
              <strong>Dr. Anukriti Sood</strong>
              <span>Breast & Thyroid Surgeon | AIIMS New Delhi</span>
            </div>
          </div>
          <div class="lfb-clist-item">
            <span class="lfb-clist-dot"></span>
            <div>
              <strong>Dr. Mansimrat P. Singh</strong>
              <span>General, Laparoscopic & Minimal Access Surgeon</span>
            </div>
          </div>
          <div class="lfb-clist-item">
            <span class="lfb-clist-dot"></span>
            <div>
              <strong>DM Certified Specialists</strong>
              <span>Neurology & Gastroenterology Wings</span>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  if (index === 2) {
    return /* html */`
      <div class="lfb-highlight-card">
        <div class="lfb-card-title">4-Phase Journey</div>
        <div class="lfb-card-timeline">
          <div class="lfb-timeline-item">
            <div class="lfb-tline-node">1</div>
            <div class="lfb-tline-text">Pre-Admission PAC Screening</div>
          </div>
          <div class="lfb-timeline-item">
            <div class="lfb-tline-node">2</div>
            <div class="lfb-tline-text">Same-Day Direct Admission</div>
          </div>
          <div class="lfb-timeline-item">
            <div class="lfb-tline-node">3</div>
            <div class="lfb-tline-text">Ultra-Minimally Invasive Exec.</div>
          </div>
          <div class="lfb-timeline-item">
            <div class="lfb-tline-node">4</div>
            <div class="lfb-tline-text">Continuous Care Loop Monitoring</div>
          </div>
        </div>
      </div>
    `;
  }

  if (index === 3) {
    return /* html */`
      <div class="lfb-highlight-card">
        <div class="lfb-card-title">Infrastructure Compliance</div>
        <div class="lfb-card-specs">
          <div class="lfb-spec-item">
            <div class="lfb-spec-icon">🧪</div>
            <div>
              <strong>Laminar Flow OTs</strong>
              <span>Class 100 sterile environments</span>
            </div>
          </div>
          <div class="lfb-spec-item">
            <div class="lfb-spec-icon">⚡</div>
            <div>
              <strong>1470nm Diode Lasers</strong>
              <span>Instant vessel sealing & bloodless cuts</span>
            </div>
          </div>
          <div class="lfb-spec-item">
            <div class="lfb-spec-icon">🔬</div>
            <div>
              <strong>4K HD NBI Endoscopy</strong>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  return '';
}

function renderLongForm(blocks, slug = 'home') {
  if (!blocks || !blocks.length) return '';
  const visuals = longFormVisuals[slug] || longFormVisuals.home;

  return /* html */`
<section class="long-form-section">
  <div class="long-form-container">
    ${blocks.map((block, i) => {
      const v = visuals[i] || visuals[visuals.length - 1];
      return /* html */`
    <div class="lfb-block" id="lfb-${i}">

      <!-- Visual column — wide cinematic visual on top -->
      <div class="lfb-visual-col">
        <div class="lfb-visual-frame">
          <img src="${v.image}" alt="${v.title}" class="lfb-img" loading="lazy">
          <div class="lfb-img-overlay"></div>
          <!-- Caption: inside .lfb-visual-frame — uses frame's overflow:hidden for clean crop -->
          <div class="lfb-caption-card">
            <div class="lfb-caption-badge">
              <span class="lfb-badge-dot"></span>
              ${v.badge}
            </div>
            <div class="lfb-caption-title">${v.title}</div>
            <div class="lfb-caption-desc">${v.desc}</div>
          </div>
        </div>
      </div>

      <!-- Text Row — side-by-side structure below the image to keep section height low -->
      <div class="lfb-text-row">
        <div class="lfb-heading-col">
          <div class="lfb-index">${String(i + 1).padStart(2, '0')}</div>
          <h2 class="lfb-heading">${block.heading_h2}</h2>
          ${getHighlightCardHTML(slug, i)}
        </div>
        <div class="lfb-content-col clinical-rich-text">
          ${parseMarkdown(block.content)}
        </div>
      </div>

    </div>`;
    }).join('')}
  </div>
</section>`;
}

/* ── TESTIMONIALS SLIDER ── */
function getTestimonialBadge(quote) {
  const text = quote.toLowerCase();
  if (text.includes('fistula')) return 'Laser Fistula (VAAFT)';
  if (text.includes('thyroid')) return 'Microwave Thyroid Ablation';
  if (text.includes('colonoscopy')) return 'Therapeutic Colonoscopy';
  if (text.includes('hernia')) return 'Laparoscopic Hernia Repair';
  if (text.includes('migraine')) return 'Neurology / Migraine Care';
  if (text.includes('breast')) return 'Microwave Breast Ablation';
  if (text.includes('endoscopy')) return 'Therapeutic Endoscopy';
  if (text.includes('varicose')) return 'Laser Varicose Veins';
  if (text.includes('bariatric')) return 'Laparoscopic Bariatric';
  if (text.includes('piles')) return 'Laser Piles Surgery (LHP)';
  return 'Verified Treatment';
}

function renderTestimonials(items = []) {
  if (!items.length) return '';
  const stars = ico.star.repeat(5);

  return /* html */`
<section class="testimonials-section">
  <div class="testimonials-inner">

    <div class="section-header centered" id="testi-header">
      <span class="section-tag">Patient Stories</span>
      <h2>What Our Patients Say</h2>
    </div>

    <div class="testi-slider-wrapper" id="testi-slider-wrapper">
      <div class="testi-slider-track" id="testi-slider-track">
        ${items.map((item, idx) => {
          const badge = getTestimonialBadge(item.quote);
          return /* html */`
        <div class="testi-slide" id="testi-slide-${idx}">
          <div class="testi-slide-inner">
            <div class="testi-slide-top">
              <div class="testi-stars">${stars}</div>
              <span class="testi-slide-badge">
                <span class="badge-dot"></span>
                ${badge}
              </span>
            </div>
            <blockquote class="testi-quote">"${item.quote}"</blockquote>
            <div class="testi-author">
              <div class="testi-avatar-sm">${item.name.charAt(0)}</div>
              <div>
                <div class="testi-author-name">${item.name}</div>
                <div class="testi-author-loc">${ico.location} ${item.location} · Verified</div>
              </div>
            </div>
          </div>
        </div>`;
        }).join('')}
      </div>
      <div class="testi-slider-nav">
        <button class="testi-nav-btn" id="testi-prev" aria-label="Previous review">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 19l-7-7 7-7"/></svg>
        </button>
        <div class="testi-dots" id="testi-dots">
          ${items.map((_, i) => `<button class="testi-dot ${i===0?'active':''}" data-idx="${i}" aria-label="Go to review ${i+1}"></button>`).join('')}
        </div>
        <button class="testi-nav-btn" id="testi-next" aria-label="Next review">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 5l7 7-7 7"/></svg>
        </button>
      </div>
    </div>

  </div>
</section>`;
}

/* ── FAQ ACCORDION — MINIMALIST NUMBERED DESIGN ── */
function renderFAQs(faqs = []) {
  if (!faqs.length) return '';

  return /* html */`
<section class="faq-section">
  <div class="faq-inner">

    <div class="faq-layout">
      <div class="faq-header-col">
        <span class="section-tag">FAQs</span>
        <h2>Common Questions</h2>
        <p>Everything you need to know about day-care surgery, anaesthesia, insurance, and recovery.</p>
        <a href="/contact/" class="btn btn-outline faq-contact-btn">Ask a Question</a>
      </div>

      <div class="faq-list" id="faq-container">
        ${faqs.map((faq, i) => /* html */`
        <div class="faq-item" id="faq-item-${i}">
          <button class="faq-trigger" aria-expanded="false">
            <span class="faq-num">${String(i + 1).padStart(2, '0')}</span>
            <span class="faq-q">${faq.question}</span>
            <span class="faq-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M6 9l6 6 6-6"/></svg>
            </span>
          </button>
          <div class="faq-body">
            <p>${faq.answer}</p>
          </div>
        </div>`).join('')}
      </div>
    </div>

  </div>
</section>`;
}

// ─────────────────────────────────────────────────────────────
// 6. PAGE TEMPLATE BUILDERS
// ─────────────────────────────────────────────────────────────

function buildHomeTemplate(data) {
  return [
    renderHero(data.hero_section, 'home'),
    renderMarquee(),
    renderParadigm(),
    renderBento(),
    renderDoctors(),
    renderLongForm(data.long_form_content_blocks, 'home'),
    renderTestimonials(data.patient_testimonials),
    renderFAQs(data.extensive_faqs),
    renderFooter(),
  ].join('\n');
}

/* ── SPECIALTY INTRO DARK SECTION ── */
function renderSpecialtyIntro(data) {
  const slug = data.page_url_slug;
  const iconMap = {
    'breast-endocrine': { icon: '🧬', tag: 'Breast & Endocrine Wing', headline: 'Where Oncoplastic Surgery Meets Innovation', desc: "MDCC's Breast & Endocrine unit is led by AIIMS alumni performing scarless robotic thyroidectomies, microwave ablation of breast/thyroid nodules, and integrated diabetic limb salvage — all as day-care procedures." },
    'laser-proctology': { icon: '⚡', tag: 'Laser Proctology & Laparoscopy', headline: 'Laser Precision. Zero Hospital Nights.', desc: 'Advanced 1470nm Diode Laser technology enables hemorrhoidoplasty, sphincter-preserving fistula sealing (FiLaC/VAAFT), and laparoscopic hernia mesh placement — all with 4\u20136 hour same-day discharge.' },
    gastroenterology:   { icon: '🔬', tag: 'Gastroenterology Wing', headline: 'Precision Endoscopy. Evidence-Based Hepatology.', desc: 'Our 4K therapeutic endoscopy suite performs ERCP, diagnostic colonoscopies, polypectomies, and advanced hepato-pancreato-biliary interventions guided by DM-certified gastroenterology faculty.' },
    neurology:          { icon: '🧠', tag: 'Neurology Department', headline: 'Comprehensive Neurological Care at MDCC', desc: 'DM Neurology-certified specialists deliver EMG/NCV electrophysiology studies, Botox migraine prophylaxis, stroke management, and neuropathy rehabilitation — under one day-care roof in Jaipur.' },
  };

  const statsMap = {
    'breast-endocrine': [
      { num: '98', suffix: '%',  label: 'MWA Success Rate' },
      { num: '0',  suffix: 'cm', label: 'Scar (Transaxillary)' },
      { num: '6',  suffix: 'hr', label: 'Avg Discharge Time' },
      { num: '3',  suffix: '+',  label: 'Specialists' },
    ],
    'laser-proctology': [
      { num: '97', suffix: '%',  label: 'Laser Procedure Success' },
      { num: '4',  suffix: 'hr', label: 'Avg Discharge Time' },
      { num: '0',  suffix: '',   label: 'General Anesthesia HAI' },
      { num: '2',  suffix: '+',  label: 'Laser Surgeons' },
    ],
    gastroenterology: [
      { num: '4K', suffix: '',   label: 'NBI Endoscopy' },
      { num: '99', suffix: '%',  label: 'Diagnostic Accuracy' },
      { num: '1',  suffix: 'dy', label: 'ERCP Turnaround' },
      { num: '1',  suffix: '',   label: 'DM Gastroenterologist' },
    ],
    neurology: [
      { num: '31', suffix: 'pts', label: 'Botox Migraine Protocol' },
      { num: '2',  suffix: 'wk',  label: 'EMG/NCV Report Time' },
      { num: '95', suffix: '%',   label: 'Patient Satisfaction' },
      { num: '1',  suffix: '',    label: 'DM Neurologist' },
    ],
  };

  const m = iconMap[slug] || iconMap['breast-endocrine'];
  const stats = statsMap[slug] || statsMap['breast-endocrine'];

  return /* html */`
<section class="specialty-intro-section">
  <div class="specialty-intro-inner">

    <div class="specialty-intro-text" id="spec-intro-text">
      <span class="section-tag">${m.tag}</span>
      <h2>${m.headline}</h2>
      <p>${m.desc}</p>
      <div class="spec-intro-ctas">
        <a href="/contact/" class="btn btn-primary">${ico.calendar} Book Consultation</a>
        <a href="/doctors/" class="btn btn-secondary">${ico.search} Meet the Specialists</a>
      </div>
    </div>

    <div class="specialty-icon-display" id="spec-intro-icon">
      <div class="spec-icon-bubble">${m.icon}</div>
    </div>

    <div class="specialty-stats-grid" id="spec-stats-grid">
      ${stats.map(s => /* html */`
      <div class="spec-stat-card">
        <div class="spec-stat-num"><span class="count-up" data-target="${parseInt(s.num) || 0}">${s.num}</span>${s.suffix}</div>
        <div class="spec-stat-label">${s.label}</div>
      </div>`).join('')}
    </div>

  </div>
</section>`;
}

function buildSpecialtyTemplate(data) {
  return [
    renderHero(data.hero_section, data.page_url_slug),
    renderMarquee(),
    renderSpecialtyIntro(data),
    renderLongForm(data.long_form_content_blocks, data.page_url_slug),
    renderDoctors(),
    renderTestimonials(data.patient_testimonials),
    renderFAQs(data.extensive_faqs),
    renderFooter(),
  ].join('\n');
}


function buildDoctorTemplate(data) {
  const slug   = data.page_url_slug;
  const meta   = doctorMetadata[slug] || {};
  const bgImg  = meta.image || routeImages[slug] || routeImages.home;
  const stars  = ico.star.repeat(5);

  // Peer doctors (other doctors at MDCC, excluding self)
  const peers = Object.entries(doctorMetadata)
    .filter(([s]) => s !== slug)
    .slice(0, 4);

  const docPhone = meta.phone || '78911 33200';
  const docPhoneRaw = meta.phoneRaw || '7891133200';

  // Credential strip items
  const credItems = [
    { icon: ico.shield, label: 'Qualifications', value: meta.credentials || 'Board Certified' },
    { icon: ico.check,  label: 'Specialty',      value: meta.specialty || data.hero_section?.h1 || 'Surgeon' },
    { icon: ico.clock,  label: 'Availability',   value: 'Mon–Sat · OPD & Surgery' },
    { icon: ico.phone,  label: 'Appointments',   value: docPhone },
  ];

  return /* html */`
<div class="doctor-hero">
  <div class="doctor-hero-img">
    <img src="${bgImg}" alt="${meta.name}" fetchpriority="high">
  </div>
  <div class="doctor-hero-overlay"></div>
  <div class="doctor-hero-noise"></div>
 
  <div class="doctor-hero-content">
    <div class="doctor-hero-badge-row" id="dhero-badges">
      <span class="doc-hero-tag">MDCC Specialist</span>
      <span class="doc-hero-tag glass">${meta.badge || 'Senior Specialist'}</span>
    </div>
    <h1 class="doctor-hero-name" id="dhero-name">${meta.name || data.hero_section?.h1 || 'MDCC Specialist'}</h1>
    <p class="doctor-hero-specialty" id="dhero-specialty">${meta.specialty} &nbsp;&middot;&nbsp; ${meta.credentials}</p>
    
    ${data.stats ? /* html */`
    <div class="doctor-hero-stats">
      <div class="doc-stat-item">
        <span class="doc-stat-val">${data.stats.experience}</span>
        <span class="doc-stat-lbl">Experience</span>
      </div>
      <div class="doc-stat-divider"></div>
      <div class="doc-stat-item">
        <span class="doc-stat-val">${data.stats.surgeries}</span>
        <span class="doc-stat-lbl">Surgeries</span>
      </div>
      <div class="doc-stat-divider"></div>
      <div class="doc-stat-item">
        <span class="doc-stat-val" style="color: #F5A623;">${data.stats.rating}</span>
        <span class="doc-stat-lbl">Avg Rating</span>
      </div>
    </div>
    ` : ''}
 
    <div class="doctor-hero-ctas" id="dhero-ctas">
      <a href="/contact/" class="btn btn-primary">${ico.calendar} Book Consultation</a>
      <a href="tel:${docPhoneRaw}" class="btn btn-secondary">${ico.phone} Call ${docPhone}</a>
    </div>
  </div>
</div>

<nav class="doctor-breadcrumb" aria-label="Breadcrumb">
  <a href="/doctors/" class="breadcrumb-link">${ico.arrow} All Specialists</a>
  <span class="breadcrumb-sep">/</span>
  <span class="breadcrumb-current">${meta.name || 'Doctor Profile'}</span>
</nav>

<div class="doctor-cred-strip">
  <div class="cred-strip-inner" id="cred-strip">
    ${credItems.map(ci => /* html */`
    <div class="cred-item">
      <div class="cred-icon">${ci.icon}</div>
      <div class="cred-text">
        <strong>${ci.label}</strong>
        <span>${ci.value}</span>
      </div>
    </div>`).join('')}
  </div>
</div>

<div class="doctor-profile-body">

  <!-- Main content -->
  <div class="doctor-profile-main" id="doc-profile-main">
    ${data.long_form_content_blocks.map((block, i) => /* html */`
    <div class="clinical-rich-block" id="cb-${i}">
      <h2>${block.heading_h2}</h2>
      <div class="clinical-rich-text">${parseMarkdown(block.content)}</div>
    </div>`).join('')}

    <!-- Visual Journey Timeline & Registration Section -->
    <div class="clinical-rich-block" id="doc-journey">
      <h2>Credentials &amp; Professional Journey</h2>
      
      <div class="registration-card-wrapper">
        <div class="registration-card">
          <div class="reg-card-icon">${ico.shield}</div>
          <div class="reg-card-info">
            <span class="reg-label">Verified Medical Registration</span>
            <h3 class="reg-number">${data.registration_number || 'RMC Registered'}</h3>
            <span class="reg-authority">Rajasthan Medical Council (RMC)</span>
          </div>
          <div class="reg-card-seal" aria-hidden="true">
            <svg viewBox="0 0 100 100" class="seal-svg">
              <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" stroke-width="1.5" stroke-dasharray="3 3"/>
              <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" stroke-width="1"/>
              <text x="50" y="46" text-anchor="middle" font-size="7" font-weight="800" letter-spacing="0.05em">MEDICAL COUNCIL</text>
              <text x="50" y="54" text-anchor="middle" font-size="7" font-weight="800" letter-spacing="0.05em">VERIFIED</text>
              <path d="M35 68 L50 60 L65 68 L60 52 L72 40 L56 38 L50 24 L44 38 L28 40 L40 52 Z" fill="rgba(var(--color-secondary-rgb), 0.12)" stroke="var(--color-secondary)" stroke-width="1"/>
            </svg>
          </div>
        </div>
      </div>

      <div class="journey-grids">
        <!-- Academic Timeline -->
        <div class="timeline-column">
          <h3 class="timeline-title">${ico.shield} Academic Pedigree</h3>
          <div class="timeline-track">
            ${data.academic_timeline?.map(item => /* html */`
            <div class="timeline-node">
              <div class="node-dot"></div>
              <div class="node-year">${item.year}</div>
              <div class="node-content">
                <h4>${item.degree}</h4>
                <span class="node-inst">${item.institution}</span>
                <p class="node-desc">${item.details}</p>
              </div>
            </div>`).join('') || ''}
          </div>
        </div>

        <!-- Career Timeline -->
        <div class="timeline-column">
          <h3 class="timeline-title">${ico.clock} Professional Milestones</h3>
          <div class="timeline-track">
            ${data.career_timeline?.map(item => /* html */`
            <div class="timeline-node">
              <div class="node-dot"></div>
              <div class="node-year">${item.year}</div>
              <div class="node-content">
                <h4>${item.role}</h4>
                <span class="node-inst">${item.organization}</span>
                <p class="node-desc">${item.details}</p>
              </div>
            </div>`).join('') || ''}
          </div>
        </div>
      </div>

      <!-- Fellowships & Board Certifications -->
      ${data.fellowships?.length ? /* html */`
      <div class="fellowships-container">
        <h3 class="timeline-title" style="margin-top: 3.5rem;">${ico.check} Fellowships &amp; Memberships</h3>
        <div class="fellowships-grid">
          ${data.fellowships.map(f => /* html */`
          <div class="fellowship-badge-card">
            <div class="f-badge-icon">${ico.check}</div>
            <span class="f-badge-text">${f}</span>
          </div>`).join('')}
        </div>
      </div>` : ''}
    </div> <!-- Close #doc-journey -->
  </div> <!-- Close .doctor-profile-main -->

  <!-- Sticky sidebar -->
  <aside class="doctor-profile-sidebar" id="doc-sidebar">
    <div class="doc-sidebar-cta-card">
      <h3>Request a Consultation</h3>
      <p>Book a modular same-day procedure or an outpatient consultation with ${meta.name?.split(' ').slice(0,2).join(' ') || 'this specialist'} at MDCC Jhotwara.</p>
      <a href="/contact/" class="btn btn-primary">${ico.calendar} Schedule Now</a>
    </div>

    <div class="doc-sidebar-peers">
      <h4>Also at MDCC</h4>
      ${peers.map(([pSlug, p]) => /* html */`
      <a href="/doctor/${pSlug}/" class="peer-item">
        <div class="peer-avatar">${p.name.charAt(4)}</div>
        <div class="peer-info">
          <span class="peer-name">${p.name}</span>
          <span class="peer-role">${p.specialty.split(',')[0]}</span>
        </div>
      </a>`).join('')}
    </div>
  </aside>
</div> <!-- Close .doctor-profile-body -->

<!-- Full width testimonials and FAQs -->
${(data.patient_testimonials?.length || data.extensive_faqs?.length) ? /* html */`
<div class="doctor-profile-fullwidth">
  ${data.patient_testimonials?.length ? /* html */`
  <div class="clinical-rich-block" id="doc-testimonials">
    <h2>Patient Recovery Accounts</h2>
    <div class="testimonials-grid" style="margin-top:2rem;">
      ${data.patient_testimonials.map(item => /* html */`
      <div class="testimonial-card">
        <div class="testimonial-stars">${stars}</div>
        <p class="testimonial-quote">"${item.quote}"</p>
        <div class="testimonial-author">
          <div class="author-avatar">${item.name.charAt(0)}</div>
          <div class="author-info">
            <h4>${item.name}</h4>
            <span>${ico.location} ${item.location} · Verified Patient</span>
          </div>
        </div>
      </div>`).join('')}
    </div>
  </div>` : ''}

  ${data.extensive_faqs?.length ? /* html */`
  <div class="clinical-rich-block" id="doc-faqs">
    <h2>Clinical FAQ</h2>
    <div class="faq-container" style="margin-top:2rem;">
      ${data.extensive_faqs.map((faq, i) => /* html */`
      <div class="faq-item">
        <button class="faq-header" aria-expanded="false">
          <span class="faq-question">${faq.question}</span>
          <span class="faq-toggle">${ico.plus}</span>
        </button>
        <div class="faq-content"><p>${faq.answer}</p></div>
      </div>`).join('')}
    </div>
  </div>` : ''}
</div>
` : ''}

${renderFooter()}`;
}

function buildDirectoryTemplate() {
  return /* html */`
<div>
  <!-- Hero Banner -->
  <div class="directory-hero" id="dir-hero">
    <div class="directory-hero-inner">
      <div class="directory-hero-text" id="dir-hero-text">
        <span class="section-tag" style="color:var(--color-secondary-light);justify-content:center;">${ico.search} Specialist Registry</span>
        <h1>Find Your <span>Expert Surgeon</span></h1>
        <p>Browse MDCC's complete faculty of board-certified specialists, senior consultants, and advanced therapeutic departments — all under one day-care roof.</p>
      </div>

      <div class="directory-search-hero" id="dir-search-hero">
        <input type="text" id="directory-search" placeholder="Search by surgeon, specialty, or treatment…" aria-label="Search Roster">
        <span class="dir-search-icon">${ico.search}</span>
      </div>

      <div class="directory-hero-stats" id="dir-hero-stats">
        <div class="dh-stat"><span>3</span> Senior Surgeons</div>
        <div class="dh-stat"><span>4</span> Super-Specialties</div>
        <div class="dh-stat">${ico.shield} AIIMS Faculty</div>
        <div class="dh-stat">${ico.clock} Same-Day Discharge</div>
      </div>
    </div>
  </div>

  <!-- Filter + Cards Body -->
  <section class="directory-section">
    <div class="filter-tabs" style="margin-bottom:2.5rem;">
      <button class="filter-tab active" data-filter="all">All</button>
      <button class="filter-tab" data-filter="specialist">Doctors</button>
      <button class="filter-tab" data-filter="department">Departments</button>
    </div>

    <div class="roster-grid" id="roster-grid">
      ${Object.entries(doctorMetadata).map(([slug, doc], idx) => {
        return /* html */`
      <div class="roster-item doctor-item"
           data-category="specialist"
           data-search="${doc.name.toLowerCase()} ${doc.specialty.toLowerCase()} ${doc.credentials.toLowerCase()}">
        <a href="/doctor/${slug}/" class="doctor-card" aria-label="${doc.name}">
          <div class="doctor-photo-frame">
            <img src="${doc.image}" alt="${doc.name}" class="doctor-photo" loading="lazy">
            <div class="doctor-photo-overlay"></div>
            <div class="doctor-badge-float">${doc.badge}</div>
          </div>
          <div class="doctor-info-wrapper">
            <div>
              <div class="doc-name">${doc.name}</div>
              <div class="doc-credentials">${doc.credentials}</div>
              <div class="doc-specialty">${doc.specialty}</div>
            </div>
            <div class="doc-action">
              <span class="action-label">View Profile</span>
              <span class="action-circle">${ico.arrowUR}</span>
            </div>
          </div>
        </a>
      </div>`;
      }).join('')}

      ${bentoSpecialties.map(dept => /* html */`
      <div class="roster-item department-item"
           data-category="department"
           data-search="${dept.title.toLowerCase()} ${dept.desc.toLowerCase()}">
        <div class="department-card">
          <div class="dept-header">
            <div class="dept-icon">${dept.icon}</div>
            <div class="dept-tag">Super-Specialty</div>
          </div>
          <h3>${dept.title}</h3>
          <p>${dept.desc}</p>
          <a href="${dept.path}" class="dept-action">
            <span class="action-text">Explore Specialty</span>
            <span class="action-arrow">${ico.arrow}</span>
          </a>
        </div>
      </div>`).join('')}
    </div>
  </section>

  <!-- CTA strip -->
  <div class="directory-cta-strip">
    <div class="cta-strip-inner">
      <div class="cta-strip-text">
        <h3>Can't find the right specialist?</h3>
        <p>Our reception team will match you with the correct surgeon within minutes.</p>
      </div>
      <a href="tel:7891133200" class="btn-ghost-white">${ico.phone} Call 78911 33200</a>
    </div>
  </div>
${renderFooter()}
</div>`;
}


function buildContactTemplate() {
  return /* html */`
<div class="contact-page-wrap">
  <!-- Hero Section using renderHero pattern -->
  ${renderHero({ h1: 'Book Your <span>Consultation</span>', subheadline: 'Schedule outpatient specialist appointments or day-care surgical admissions at MDCC.' }, 'contact')}

  <!-- Modern Bento-style Body -->
  <div class="contact-body bento-layout">
    
    <!-- Left Column: Booking Form -->
    <div class="contact-main-col">
      <div class="booking-form-card" id="booking-form-wrap" style="position:relative;">
        <h2>Request Appointment</h2>
        <p class="form-subheadline">Fill in your details and our coordinator will confirm your slot within 2 hours.</p>

        <form class="booking-form" id="booking-form" novalidate>
          <div class="form-row">
            <div class="form-group">
              <label for="patient-name">Patient Full Name *</label>
              <input type="text" id="patient-name" class="input-field" placeholder="e.g. Rajesh Kumar" required>
            </div>
            <div class="form-group">
              <label for="patient-phone">Mobile Number *</label>
              <input type="tel" id="patient-phone" class="input-field" placeholder="10-digit number" required pattern="[0-9]{10}">
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label for="booking-department">Clinical Specialty *</label>
              <select id="booking-department" class="input-field" required>
                <option value="" disabled selected>Select specialty…</option>
                ${bentoSpecialties.map(d => `<option value="${d.title}">${d.title}</option>`).join('')}
              </select>
            </div>
            <div class="form-group">
              <label for="booking-doctor">Consulting Specialist *</label>
              <select id="booking-doctor" class="input-field" required disabled>
                <option value="" disabled selected>Select specialty first…</option>
              </select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label for="booking-date">Requested Date *</label>
              <input type="date" id="booking-date" class="input-field" required>
            </div>
            <div class="form-group">
              <label for="booking-time">Consultation Shift *</label>
              <select id="booking-time" class="input-field" required>
                <option value="" disabled selected>Select shift…</option>
                <option>Morning Shift (09:00 AM – 01:00 PM)</option>
                <option>Afternoon Shift (01:00 PM – 05:00 PM)</option>
                <option>Evening Shift (05:00 PM – 09:00 PM)</option>
              </select>
            </div>
          </div>
          <div class="form-group">
            <label for="patient-symptoms">Case History / Pathology Description</label>
            <textarea id="patient-symptoms" class="input-field"
              placeholder="e.g. Diabetic foot ulcer management, laser fistula evaluation, thyroid nodule MWA…"></textarea>
          </div>
          <button type="submit" class="btn btn-primary" style="width:100%;justify-content:center;padding:1.1rem;">
            ${ico.calendar} Confirm Booking
          </button>
        </form>

        <div class="success-overlay" id="success-overlay">
          <div class="success-card">
            <div class="success-icon">✓</div>
            <h3>Appointment Requested!</h3>
            <p>Your appointment request has been received. Our team will call you within 2 hours to confirm your slot at 78911 33200.</p>
            <div class="success-details">
              <div class="details-row"><span class="label">Patient:</span><span class="val" id="res-name">-</span></div>
              <div class="details-row"><span class="label">Phone:</span><span class="val" id="res-phone">-</span></div>
              <div class="details-row"><span class="label">Department:</span><span class="val" id="res-dept">-</span></div>
              <div class="details-row"><span class="label">Specialist:</span><span class="val" id="res-doctor">-</span></div>
              <div class="details-row"><span class="label">Slot:</span><span class="val" id="res-slot">-</span></div>
            </div>
            <a href="/" class="btn btn-primary" style="width:100%;justify-content:center;">
              ${ico.home} Return to Homepage
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Column: Info & Location -->
    <div class="contact-side-col">
      <div class="clinic-info-card">
        <a href="https://maps.google.com/?q=MDCC+Indrapura+Jhotwara+Jaipur" target="_blank" rel="noopener" class="clinic-map-thumb" aria-label="Open location in Google Maps">
          <span class="map-pin-icon">📍</span>
          <span class="map-overlay-text">📍 Open in Google Maps</span>
        </a>
        <div class="clinic-details">
          <div class="clinic-name">Medical Day Care Center (MDCC)</div>
          <div class="clinic-address">Kalwar Road, Indrapura, Jhotwara,<br>Jaipur, Rajasthan – 302012</div>
        </div>
      </div>

      <div class="clinic-hours-card">
        <h4>Consultation Hours</h4>
        <div class="hours-row"><span class="day">Monday – Saturday</span><span class="time">9:00 AM – 9:00 PM</span></div>
        <div class="hours-row"><span class="day">Sunday</span><span class="time closed">By Appointment</span></div>
        <div class="hours-row" style="border-top:1px dashed rgba(var(--color-primary-rgb),0.15);margin-top:0.75rem;padding-top:0.75rem;">
          <span class="day" style="font-weight:700;color:var(--color-primary);">Surgical Listing</span>
          <span class="time" style="font-weight:700;color:var(--color-secondary);">7:00 AM Onwards</span>
        </div>
      </div>

      <div class="contact-trust-badges">
        <div class="trust-badge-item">
          <div class="tb-icon">${ico.clock}</div>
          <div class="tb-text"><strong>Same-Day Discharge</strong><span>Home within 4–8 hrs of surgery</span></div>
        </div>
      </div>
    </div>

  </div>
  ${renderFooter()}
</div>`;
}

// ─────────────────────────────────────────────────────────────
// 6b. FOOTER RENDERER
// ─────────────────────────────────────────────────────────────
function renderFooter() {
  return /* html */`
<footer class="site-footer" role="contentinfo">
  <div class="footer-top">
    <div class="footer-brand">
      <div class="footer-logo">
        <div class="logo-bg-wrapper">
          <img src="/images/mdcc logo.png" alt="MDCC Logo" class="mdcc-logo-img" />
        </div>
        <div class="logo-text">
          <span class="logo-main">MDCC</span>
          <span class="logo-sub">Medical Day Care Center</span>
        </div>
      </div>
      <p>Jaipur's premier day-care surgical centre — advanced procedures, same-day discharge, zero hospital nights. Trusted by thousands of patients across Rajasthan.</p>
      <div class="footer-badges">
        <span class="f-badge">AIIMS Alumni Faculty</span>
        <span class="f-badge">Laser Proctology</span>
        <span class="f-badge">MWA Ablation</span>
        <span class="f-badge">4K Endoscopy</span>
      </div>
    </div>

    <div class="footer-nav">
      <h4>Specialties</h4>
      <ul>
        <li><a href="/specialty/breast-endocrine/">Breast &amp; Endocrine Surgery</a></li>
        <li><a href="/specialty/laser-proctology/">Laser Proctology</a></li>
        <li><a href="/specialty/general-surgery/">General Surgery &amp; Wound Care</a></li>
      </ul>
    </div>

    <div class="footer-nav">
      <h4>Quick Links</h4>
      <ul>
        <li><a href="/">Home</a></li>
        <li><a href="/doctors/">Find a Doctor</a></li>
        <li><a href="/contact/">Book Appointment</a></li>
      </ul>
    </div>

    <div class="footer-contact">
      <h4>Contact Us</h4>
      <div class="contact-item">
        <div class="ci-icon">${ico.location}</div>
        <div class="ci-text">
          <strong>Address</strong>
          <span>Kalwar Road, Indrapura, Jhotwara,<br>Jaipur, Rajasthan – 302012</span>
        </div>
      </div>
      <div class="contact-item">
        <div class="ci-icon">${ico.phone}</div>
        <div class="ci-text">
          <strong>Phone</strong>
          <span><a href="tel:7891133200" style="color:rgba(255,255,255,0.75);">78911 33200</a></span>
        </div>
      </div>
      <div class="contact-item">
        <div class="ci-icon">${ico.clock}</div>
        <div class="ci-text">
          <strong>OPD Hours</strong>
          <span>Mon–Sat: 9AM–9PM &amp; Sun: By Appointment</span>
        </div>
      </div>
    </div>
  </div>

  <div class="footer-divider"></div>

  <div class="footer-bottom">
    <p>&copy; ${new Date().getFullYear()} Medical Day Care Center (MDCC), Jaipur. All rights reserved. Developed and maintained by <a href="https://revngrow.com" target="_blank" rel="noopener noreferrer" style="color: inherit; text-decoration: underline;">Rev n Grow</a>.</p>
    <div class="footer-bottom-links">
      <a href="/">Privacy Policy</a>
      <a href="/">Terms of Use</a>
    </div>
  </div>
</footer>`;
}

// ─────────────────────────────────────────────────────────────
// 7. INTERACTION ENGINE
// ─────────────────────────────────────────────────────────────

function initStickyHeader() {
  const header = document.querySelector('.site-header');
  if (!header) return;
  const onScroll = () => header.classList.toggle('scrolled', window.scrollY > 50);
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

function initMobileNav() {
  const toggle  = document.querySelector('.mobile-nav-toggle');
  const overlay = document.querySelector('.mobile-menu-overlay');
  if (!toggle || !overlay) return;

  const fresh = toggle.cloneNode(true);
  toggle.parentNode.replaceChild(fresh, toggle);

  fresh.addEventListener('click', () => {
    const open = fresh.classList.toggle('active');
    overlay.classList.toggle('active', open);
    document.body.style.overflow = open ? 'hidden' : '';
    fresh.setAttribute('aria-expanded', String(open));
  });

  overlay.querySelectorAll('a').forEach(a => {
    a.addEventListener('click', () => {
      fresh.classList.remove('active');
      overlay.classList.remove('active');
      document.body.style.overflow = '';
      fresh.setAttribute('aria-expanded', 'false');
    });
  });
}

function highlightActiveLinks(hash) {
  document.querySelectorAll('.nav-link, .mobile-menu-link, .bottom-bar-link').forEach(link => {
    const href = link.getAttribute('href');
    const isActive = href === hash || (hash.startsWith(href) && href !== '#/');
    link.classList.toggle('active', isActive);
  });
}

function initAccordions() {
  // Support both old .faq-header (doctor pages) and new .faq-trigger (main FAQ)
  const triggers = [
    ...document.querySelectorAll('.faq-trigger'),
    ...document.querySelectorAll('.faq-header'),
  ];
  triggers.forEach(btn => {
    btn.addEventListener('click', () => {
      const item    = btn.closest('.faq-item');
      const body    = item.querySelector('.faq-body, .faq-content');
      const isOpen  = item.classList.contains('active');

      // close all others in same container
      const container = item.closest('#faq-container, .faq-list, .faq-container');
      if (container) {
        container.querySelectorAll('.faq-item.active').forEach(other => {
          if (other !== item) {
            other.classList.remove('active');
            const otherBody = other.querySelector('.faq-body, .faq-content');
            if (otherBody) otherBody.style.maxHeight = null;
            other.querySelector('.faq-trigger, .faq-header')?.setAttribute('aria-expanded', 'false');
          }
        });
      }

      item.classList.toggle('active', !isOpen);
      btn.setAttribute('aria-expanded', String(!isOpen));
      if (body) body.style.maxHeight = isOpen ? null : body.scrollHeight + 'px';

      setTimeout(() => ScrollTrigger?.refresh?.(), 400);
    });
  });
}

function initTestiSlider() {
  const track  = document.getElementById('testi-slider-track');
  const prev   = document.getElementById('testi-prev');
  const next   = document.getElementById('testi-next');
  const dots   = document.querySelectorAll('.testi-dot');
  if (!track || !prev || !next) return;

  const slides = track.querySelectorAll('.testi-slide');
  let current = 0;
  let autoTimer = null;

  function goTo(idx) {
    const total = slides.length;
    const visibleCards = window.innerWidth >= 1024 ? 3 : window.innerWidth >= 768 ? 2 : 1;
    const maxIdx = Math.max(0, total - visibleCards);
    
    current = Math.max(0, Math.min(idx, maxIdx));
    
    if (slides[0]) {
      const slideWidth = slides[0].getBoundingClientRect().width;
      const gap = parseFloat(window.getComputedStyle(track).gap) || 0;
      const offset = current * (slideWidth + gap);
      track.style.transform = `translateX(-${offset}px)`;
    }
    
    dots.forEach((d, i) => d.classList.toggle('active', i === current));
  }

  prev.addEventListener('click', () => {
    const visibleCards = window.innerWidth >= 1024 ? 3 : window.innerWidth >= 768 ? 2 : 1;
    const maxIdx = Math.max(0, slides.length - visibleCards);
    if (current === 0) {
      goTo(maxIdx);
    } else {
      goTo(current - 1);
    }
    resetAuto();
  });

  next.addEventListener('click', () => {
    const visibleCards = window.innerWidth >= 1024 ? 3 : window.innerWidth >= 768 ? 2 : 1;
    const maxIdx = Math.max(0, slides.length - visibleCards);
    if (current >= maxIdx) {
      goTo(0);
    } else {
      goTo(current + 1);
    }
    resetAuto();
  });

  dots.forEach(d => d.addEventListener('click', () => {
    goTo(+d.dataset.idx);
    resetAuto();
  }));

  window.addEventListener('resize', () => {
    goTo(current);
  });

  // Touch/drag support
  let startX = 0;
  track.addEventListener('touchstart', e => { startX = e.touches[0].clientX; }, { passive: true });
  track.addEventListener('touchend', e => {
    const diff = startX - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        const visibleCards = window.innerWidth >= 1024 ? 3 : window.innerWidth >= 768 ? 2 : 1;
        const maxIdx = Math.max(0, slides.length - visibleCards);
        goTo(current >= maxIdx ? 0 : current + 1);
      } else {
        const visibleCards = window.innerWidth >= 1024 ? 3 : window.innerWidth >= 768 ? 2 : 1;
        const maxIdx = Math.max(0, slides.length - visibleCards);
        goTo(current === 0 ? maxIdx : current - 1);
      }
      resetAuto();
    }
  }, { passive: true });

  function resetAuto() {
    clearInterval(autoTimer);
    autoTimer = setInterval(() => {
      const visibleCards = window.innerWidth >= 1024 ? 3 : window.innerWidth >= 768 ? 2 : 1;
      const maxIdx = Math.max(0, slides.length - visibleCards);
      goTo(current >= maxIdx ? 0 : current + 1);
    }, 6000);
  }

  const wrapper = document.getElementById('testi-slider-wrapper');
  if (wrapper) {
    wrapper.addEventListener('mouseenter', () => clearInterval(autoTimer));
    wrapper.addEventListener('mouseleave', () => resetAuto());
  }

  resetAuto();
}

function initDoctorMobileSlider() {
  const grid = document.getElementById('doctors-grid');
  const dots = document.querySelectorAll('.doctor-dot');
  if (!grid || dots.length === 0) return;

  grid.addEventListener('scroll', () => {
    const card = grid.querySelector('.doctor-card');
    if (!card) return;
    const cardWidth = card.getBoundingClientRect().width;
    const gap = parseFloat(window.getComputedStyle(grid).gap) || 0;
    const scrollLeft = grid.scrollLeft;
    const index = Math.round(scrollLeft / (cardWidth + gap));
    dots.forEach((dot, idx) => {
      dot.classList.toggle('active', idx === index);
    });
  }, { passive: true });

  dots.forEach((dot, idx) => {
    dot.addEventListener('click', () => {
      const card = grid.querySelector('.doctor-card');
      if (!card) return;
      const cardWidth = card.getBoundingClientRect().width;
      const gap = parseFloat(window.getComputedStyle(grid).gap) || 0;
      grid.scrollTo({
        left: idx * (cardWidth + gap),
        behavior: 'smooth'
      });
    });
  });
}

function animateCounters() {
  document.querySelectorAll('.count-up:not([data-counted])').forEach(el => {
    el.setAttribute('data-counted', '1');
    const target = parseInt(el.dataset.target || '0', 10);

    gsap.fromTo(el,
      { innerText: 0 },
      {
        innerText: target,
        duration: 2.2,
        ease: 'power2.out',
        snap: { innerText: 1 },
        scrollTrigger: { trigger: el, start: 'top 88%', once: true },
        onUpdate() { el.innerText = Math.round(parseFloat(this.targets()[0].innerText)); },
      }
    );
  });
}

function initMagneticBtns() {
  document.querySelectorAll('.btn-primary').forEach(btn => {
    btn.addEventListener('mousemove', e => {
      const r = btn.getBoundingClientRect();
      gsap.to(btn, { x: (e.clientX - r.left - r.width / 2) * 0.25, y: (e.clientY - r.top - r.height / 2) * 0.2, duration: 0.4, ease: 'power2.out' });
    });
    btn.addEventListener('mouseleave', () => {
      gsap.to(btn, { x: 0, y: 0, duration: 0.65, ease: 'elastic.out(1,0.5)' });
    });
  });
}

// ─────────────────────────────────────────────────────────────
// 8. GSAP SCROLL ANIMATIONS
// ─────────────────────────────────────────────────────────────
function initScrollAnimations() {
  ScrollTrigger.getAll().forEach(t => t.kill());

  /* helper: always animate TO visible state */
  const sw = (els, trigger, from, overrides = {}) => {
    const nodes = typeof els === 'string' ? [...document.querySelectorAll(els)] : [els].flat();
    if (!nodes.length) return;
    const tEl = typeof trigger === 'string' ? document.querySelector(trigger) : trigger;
    if (!tEl) return;
    gsap.fromTo(nodes,
      { opacity: 0, y: 0, x: 0, scale: 1, ...from },
      { opacity: 1, y: 0, x: 0, scale: 1, duration: 0.9, ease: 'power3.out', stagger: 0,
        ...overrides,
        scrollTrigger: { trigger: tEl, start: 'top 86%', once: true, ...(overrides.scrollTrigger || {}) } }
    );
  };

  /* — Hero: no scroll trigger, fires immediately — */
  const heroEls = [...document.querySelectorAll(
    '#hero-badge, .h1-line, .h1-accent, #hero-sub, #hero-ctas, #hero-trust, #hero-stats'
  )];
  if (heroEls.length) {
    gsap.fromTo(heroEls,
      { y: 40, opacity: 0 },
      { y: 0, opacity: 1, duration: 1.1, stagger: 0.11, ease: 'power4.out', delay: 0.2 }
    );
  }

  /* — Paradigm — */
  sw('#paradigm-visual', '#paradigm', { x: -55 }, { duration: 1.1, scrollTrigger: { start: 'top 82%' } });
  sw(
    ['#paradigm-text .section-tag','#paradigm-text h2','#paradigm-text > p',
     '#paradigm-metrics','#paradigm-capsules','.paradigm-testimonial'].map(s => document.querySelector(s)).filter(Boolean),
    document.querySelector('#paradigm-text'),
    { y: 30 },
    { stagger: 0.09, scrollTrigger: { start: 'top 80%' } }
  );
  // Parallax badges (decorative — no opacity)
  const pfTL = document.querySelector('#pfloat-tl');
  const pfBR = document.querySelector('#pfloat-br');
  if (pfTL) gsap.to(pfTL, { y: -40, ease: 'none', scrollTrigger: { trigger: '#paradigm', start: 'top bottom', end: 'bottom top', scrub: 1.2 } });
  if (pfBR) gsap.to(pfBR, { y:  40, ease: 'none', scrollTrigger: { trigger: '#paradigm', start: 'top bottom', end: 'bottom top', scrub: 1.2 } });

  /* — Bento — */
  sw('#bento-header > *', '#bento-header', { y: 26 }, { stagger: 0.08 });
  sw('.bento-item', '#bento-grid', { y: 48 }, { stagger: 0.09, duration: 0.8 });

  /* — Doctor cards — */
  sw('#doctors-header > *', '#doctors-header', { y: 26 }, { stagger: 0.08 });
  sw('.doctor-card', '#doctors-grid', { y: 52 }, { stagger: 0.1 });

  /* — Long-form editorial blocks — */
  document.querySelectorAll('.lfb-block').forEach(block => {
    const visualCol     = block.querySelector('.lfb-visual-col');
    const headingCol    = block.querySelector('.lfb-heading-col');
    const highlightCard = block.querySelector('.lfb-highlight-card');
    const contentCol    = block.querySelector('.lfb-content-col');
    const img           = block.querySelector('.lfb-img');
    const caption       = block.querySelector('.lfb-caption-card');

    if (visualCol)     sw(visualCol,     block, { scale: 0.96, y: 24 }, { duration: 1.05 });
    if (headingCol)    sw(headingCol,    block, { y: 20 },               { duration: 0.9 });
    if (highlightCard) sw(highlightCard, block, { scale: 0.95, y: 15 },  { duration: 1.0, delay: 0.2 });
    if (contentCol)    sw(contentCol,    block, { y: 24 },               { duration: 1.0, delay: 0.15 });
    if (caption)       sw(caption,       block, { y: 16 },               { delay: 0.3 });
    if (img) {
      gsap.fromTo(img, { yPercent: -8 }, {
        yPercent: 8, ease: 'none',
        scrollTrigger: { trigger: block, start: 'top bottom', end: 'bottom top', scrub: true },
      });
    }
  });


  /* — Testimonials slider — */
  sw('#testi-header > *', '#testi-header', { y: 22 }, { stagger: 0.08 });
  sw('#testi-slider-wrapper', '.testimonials-inner', { y: 32 }, { duration: 0.9 });

  /* — FAQ — */
  sw('.faq-item', '.faq-list, #faq-container', { y: 18 }, { stagger: 0.065, duration: 0.65 });
  sw('.faq-header-col > *', '.faq-header-col', { y: 24 }, { stagger: 0.08 });

  /* — Doctor profile rich blocks — */
  document.querySelectorAll('.clinical-rich-block').forEach(block => {
    gsap.fromTo(block,
      { y: 28, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.9, ease: 'power3.out',
        scrollTrigger: { trigger: block, start: 'top 85%', once: true } }
    );
  });

  /* — Doctor Profile hero (new cinematic hero) — */
  const dheroEls = [...document.querySelectorAll('#dhero-badges, #dhero-name, #dhero-specialty, #dhero-ctas')];
  if (dheroEls.length) {
    gsap.fromTo(dheroEls,
      { y: 35, opacity: 0 },
      { y: 0, opacity: 1, duration: 1, stagger: 0.1, ease: 'power4.out', delay: 0.3 }
    );
  }
  sw('.cred-item', '#cred-strip', { y: 16 }, { stagger: 0.07, duration: 0.7 });
  sw('#doc-sidebar > *', '#doc-sidebar', { x: 30 }, { stagger: 0.1, duration: 0.8 });

  /* — Directory hero — */
  const dirHeroEls = [...document.querySelectorAll('#dir-hero-text, #dir-search-hero, #dir-hero-stats')];
  if (dirHeroEls.length) {
    gsap.fromTo(dirHeroEls,
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.9, stagger: 0.12, ease: 'power3.out', delay: 0.2 }
    );
  }
  sw('.dh-stat',   '#dir-hero-stats', { y: 15 }, { stagger: 0.06, duration: 0.6 });
  sw('.roster-item', '#roster-grid',  { y: 40 }, { stagger: 0.06, duration: 0.8 });

  /* — Directory CTA strip — */
  sw('.cta-strip-text', '.directory-cta-strip', { x: -30 }, { duration: 0.9 });
  sw('.btn-ghost-white', '.directory-cta-strip', { x: 30  }, { duration: 0.9 });

  /* — Contact page — */
  const contactHero = document.querySelector('#contact-hero .contact-hero-content');
  if (contactHero) {
    gsap.fromTo([...contactHero.children],
      { y: 28, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.9, stagger: 0.1, ease: 'power3.out', delay: 0.25 }
    );
  }
  sw('#contact-trust > *', '#contact-trust', { y: 24 }, { stagger: 0.12, duration: 0.85 });
  sw('#booking-form-wrap', '.contact-body',  { x: 30 }, { duration: 0.95 });
  sw('.trust-badge-item', '.contact-trust-badges', { x: -20 }, { stagger: 0.08, duration: 0.7 });

  /* — Specialty intro section — */
  sw('#spec-intro-text > *', '#spec-intro-text', { y: 25 }, { stagger: 0.08, duration: 0.85 });
  sw('#spec-intro-icon', '.specialty-intro-section', { scale: 0.8 }, { duration: 1.1, ease: 'back.out(1.4)' });
  sw('.spec-stat-card', '#spec-stats-grid', { y: 32 }, { stagger: 0.08, duration: 0.75 });

  animateCounters();
  initMagneticBtns();

  // Double rAF ensures browser has painted new DOM before ScrollTrigger measures
  requestAnimationFrame(() => requestAnimationFrame(() => ScrollTrigger.refresh()));
}

// ─────────────────────────────────────────────────────────────
// 9. DIRECTORY SEARCH
// ─────────────────────────────────────────────────────────────
function initDirectorySearch() {
  const input = document.getElementById('directory-search');
  const tabs  = document.querySelectorAll('.filter-tab');
  const items = document.querySelectorAll('.roster-item');
  const grid  = document.getElementById('roster-grid');

  let q = '', filter = 'all';

  let noRes = grid?.querySelector('.no-results');
  if (!noRes && grid) {
    noRes = Object.assign(document.createElement('div'), { className: 'no-results' });
    noRes.style.display = 'none';
    noRes.innerHTML = '<h3>No Record Found</h3><p>Try adjusting your search or filter.</p>';
    grid.appendChild(noRes);
  }

  const apply = () => {
    let n = 0;
    items.forEach(item => {
      const show = item.dataset.search.includes(q) &&
                   (filter === 'all' || item.dataset.category === filter);
      item.style.display = show ? 'block' : 'none';
      if (show) { n++; gsap.to(item, { opacity: 1, scale: 1, duration: 0.35 }); }
      else { item.style.opacity = '0'; }
    });
    if (noRes) noRes.style.display = n === 0 ? 'block' : 'none';
  };

  input?.addEventListener('input', e => { q = e.target.value.toLowerCase().trim(); apply(); });
  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      tabs.forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      filter = tab.dataset.filter;
      apply();
    });
  });

  // Handle URL param ?filter=...
  const hash = window.location.hash;
  if (hash.includes('?')) {
    const p = new URLSearchParams(hash.split('?')[1]).get('filter');
    if (p) {
      const found = Array.from(tabs).find(t =>
        t.dataset.filter === p ||
        (p === 'departments' && t.dataset.filter === 'department') ||
        (p === 'specialists' && t.dataset.filter === 'specialist')
      );
      if (found) found.click();
    }
  }
}

// ─────────────────────────────────────────────────────────────
// 10. BOOKING FORM
// ─────────────────────────────────────────────────────────────
function initBookingForm() {
  const deptSel = document.getElementById('booking-department');
  const docSel  = document.getElementById('booking-doctor');
  const form    = document.getElementById('booking-form');
  const success = document.getElementById('success-overlay');
  const dateFld = document.getElementById('booking-date');

  if (dateFld) {
    const d = new Date(); d.setDate(d.getDate() + 1);
    const str = [d.getFullYear(), String(d.getMonth()+1).padStart(2,'0'), String(d.getDate()).padStart(2,'0')].join('-');
    dateFld.min = dateFld.value = str;
  }

  deptSel?.addEventListener('change', () => {
    const doctors = deptDoctorsMap[deptSel.value] || [];
    docSel.innerHTML = '<option value="" disabled selected>Select specialist…</option>';
    doctors.forEach(d => {
      const o = document.createElement('option');
      o.value = o.textContent = d.name;
      docSel.appendChild(o);
    });
    docSel.disabled = false;
  });

  form?.addEventListener('submit', e => {
    e.preventDefault();
    const name  = document.getElementById('patient-name').value.trim();
    const phone = document.getElementById('patient-phone').value.trim();
    const dept  = deptSel.value;
    const doc   = docSel.value;
    const date  = dateFld?.value || '';
    const time  = document.getElementById('booking-time').value;

    if (!name || !phone || !dept || !doc || !date || !time) {
      alert('Please fill in all required fields (marked with *).'); return;
    }
    if (!/^\d{10}$/.test(phone)) {
      alert('Please enter a valid 10-digit mobile number.'); return;
    }

    localStorage.setItem('mdcc_booking', JSON.stringify({ name, phone, dept, doc, date, time }));
    document.getElementById('res-name').textContent   = name;
    document.getElementById('res-phone').textContent  = phone;
    document.getElementById('res-dept').textContent   = dept;
    document.getElementById('res-doctor').textContent = doc;
    document.getElementById('res-slot').textContent   = `${date} (${time.split(' ')[0]})`;
    success.classList.add('active');
  });
}

// ─────────────────────────────────────────────────────────────
// 11. ROUTER
// ─────────────────────────────────────────────────────────────
async function loadPage(route) {
  await gsap.to(appRoot, { opacity: 0, y: -12, duration: 0.28, ease: 'power2.in' });

  const isDynamic = route.startsWith('/directory') || route.startsWith('/contact');
  let dataPath = '/content/home.json';

  if (!isDynamic) {
    if (route.startsWith('/doctor/'))    dataPath = `/content/doctors/${route.replace('/doctor/', '')}.json`;
    else if (route.startsWith('/specialty/')) dataPath = `/content/specialties/${route.replace('/specialty/', '')}.json`;
  }

  try {
    const res  = await fetch(dataPath);
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${dataPath}`);
    const data = await res.json();

    // Meta
    document.title = route.startsWith('/directory') ? 'Search Faculty Registry | MDCC Jaipur'
                   : route.startsWith('/contact')   ? 'Book Consultation | MDCC Jaipur'
                   : data.meta_title                || 'Medical Day Care Center | MDCC Jaipur';

    let metaEl = document.querySelector('meta[name="description"]');
    if (!metaEl) { metaEl = document.createElement('meta'); metaEl.name = 'description'; document.head.appendChild(metaEl); }
    metaEl.content = data.meta_description || '';

    // Render
    if (route.startsWith('/directory')) {
      appRoot.innerHTML = buildDirectoryTemplate();
      initDirectorySearch();
    } else if (route.startsWith('/contact')) {
      appRoot.innerHTML = buildContactTemplate();
      initBookingForm();
    } else if (data.page_url_slug === 'home') {
      appRoot.innerHTML = buildHomeTemplate(data);
    } else if (route.startsWith('/doctor/')) {
      appRoot.innerHTML = buildDoctorTemplate(data);
    } else if (route.startsWith('/specialty/')) {
      appRoot.innerHTML = buildSpecialtyTemplate(data);
    }

    lenis ? lenis.scrollTo(0, { immediate: true }) : window.scrollTo(0, 0);

    initMobileNav();
    initAccordions();
    initTestiSlider();
    initDoctorMobileSlider();
    initScrollAnimations();
    highlightActiveLinks('#' + route);

    gsap.fromTo(appRoot, { opacity: 0, y: 22 }, { opacity: 1, y: 0, duration: 0.65, ease: 'power3.out' });

  } catch (err) {
    console.error('[MDCC Router]', err);
    appRoot.innerHTML = /* html */`
      <section class="hero-section" style="min-height:70vh;display:flex;align-items:center;justify-content:center;">
        <div class="hero-content" style="display:block;text-align:center;">
          <div class="hero-badge" style="display:inline-flex;margin:0 auto 2rem;">
            <span class="badge-pulse"></span>Error
          </div>
          <h1 class="hero-h1"><span class="h1-accent">Page Not Found</span></h1>
          <p class="hero-sub" style="max-width:480px;margin:0 auto 2rem;">
            The requested clinical resource could not be found in our MDCC database.
          </p>
          <a href="/" class="btn btn-primary" style="margin-top:1rem;">${ico.home} Return to Home</a>
        </div>
      </section>`;
    gsap.to(appRoot, { opacity: 1, y: 0, duration: 0.4 });
  }
}

function initRouter() {
  const resolve = () => {
    let route = window.location.pathname.replace(/^\/|\/$/g, ''); if (!route) route = 'home';
    if (route.includes('?')) route = route.split('?')[0];
    loadPage(route);
  };
  
  window.addEventListener('load', resolve);
  resolve();
}

// ─────────────────────────────────────────────────────────────
// 12. BOOT
// ─────────────────────────────────────────────────────────────
document.addEventListener('DOMContentLoaded', () => {
  initLenis();
  initStickyHeader();
  initMobileNav();
  if (appRoot) initRouter();
  else { initAccordions(); initScrollAnimations(); }
});
